import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../hooks/useToast';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function RecipientListModal({ campaignId, campaignType, onClose, onSave }) {
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState([]);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [selectedSearch, setSelectedSearch] = useState('');
  const [candidateSelectedIds, setCandidateSelectedIds] = useState([]);
  const [selectedRemovedIds, setSelectedRemovedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { success, error: toastError } = useToast();

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/campaigns/${campaignId}/candidates`, {
        credentials: 'include'
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to fetch candidates');
      }

      const data = await res.json();
      setCandidates(data.candidates || []);
      setSelected(data.selected || []);
    } catch (err) {
      toastError(err.message);
    } finally {
      setLoading(false);
    }
  }, [campaignId, toastError]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const filteredCandidates = candidates.filter(c => {
    if (!candidateSearch.trim()) return true;
    const search = candidateSearch.toLowerCase();
    const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
    const phone = (c.mobile_phone || c.home_phone || '').toLowerCase();
    return name.includes(search) || phone.includes(search);
  });

  const filteredSelected = selected.filter(c => {
    if (!selectedSearch.trim()) return true;
    const search = selectedSearch.toLowerCase();
    const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
    const phone = (c.mobile_phone || c.home_phone || '').toLowerCase();
    return name.includes(search) || phone.includes(search);
  });

  const handleAddSelected = () => {
    const idsToAdd = candidateSelectedIds;
    const toMove = candidates.filter(c => idsToAdd.includes(c.id));
    const remaining = candidates.filter(c => !idsToAdd.includes(c.id));

    setSelected(prev => [...prev, ...toMove]);
    setCandidates(remaining);
    setCandidateSelectedIds([]);
  };

  const handleRemoveSelected = () => {
    const idsToRemove = selectedRemovedIds;
    const toMove = selected.filter(c => idsToRemove.includes(c.id));
    const remaining = selected.filter(c => !idsToRemove.includes(c.id));

    setCandidates(prev => [...prev, ...toMove]);
    setSelected(remaining);
    setSelectedRemovedIds([]);
  };

  const handleSelectAllCandidates = () => {
    if (candidateSelectedIds.length === filteredCandidates.length) {
      setCandidateSelectedIds([]);
    } else {
      setCandidateSelectedIds(filteredCandidates.map(c => c.id));
    }
  };

  const handleSelectAllSelected = () => {
    if (selectedRemovedIds.length === filteredSelected.length) {
      setSelectedRemovedIds([]);
    } else {
      setSelectedRemovedIds(filteredSelected.map(c => c.id));
    }
  };

  const handleAddAll = () => {
    setSelected(prev => [...prev, ...candidates]);
    setCandidates([]);
    setCandidateSelectedIds([]);
  };

  const handleRemoveAll = () => {
    setCandidates(prev => [...prev, ...selected]);
    setSelected([]);
    setSelectedRemovedIds([]);
  };

  const handleSave = async () => {
    if (selected.length === 0) {
      toastError('Please select at least one recipient');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/campaigns/${campaignId}/save-recipients`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerIds: selected.map(c => c.id) })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save recipients');
      }

      success(`Saved ${selected.length} recipients`);
      onSave();
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const getPhone = (customer) => {
    return customer.phone_number || '-';
  };

  const customerName = (c) => {
    return `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unknown';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Select Recipients ({campaignType === 'sms' ? 'SMS' : 'Voice'})
            </h2>
            <p className="text-sm text-gray-500">
              {selected.length} of {candidates.length + selected.length} customers selected
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Body - Two Column Layout */}
        <div className="flex-1 overflow-hidden px-6 py-4">
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="flex gap-4 h-full">
              {/* Left Column - Candidates */}
              <div className="flex-1 flex flex-col">
                <div className="mb-2">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Candidates ({candidates.length})
                  </h3>
                </div>
                <input
                  type="text"
                  placeholder="Search candidates..."
                  value={candidateSearch}
                  onChange={(e) => setCandidateSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={candidateSelectedIds.length === filteredCandidates.length && filteredCandidates.length > 0}
                      onClick={handleSelectAllCandidates}
                      className="rounded border-gray-300"
                    />
                    Select All
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={handleAddAll}
                      disabled={candidates.length === 0}
                      className="px-3 py-1 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {'Add All >>'}
                    </button>
                    <button
                      onClick={handleAddSelected}
                      disabled={candidateSelectedIds.length === 0}
                      className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {'Add Selected >'}
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg">
                  {filteredCandidates.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-400">
                      {candidates.length === 0 ? 'No candidates available' : 'No matches found'}
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <tbody>
                        {filteredCandidates.map(c => (
                          <tr
                            key={c.id}
                            className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                              candidateSelectedIds.includes(c.id) ? 'bg-indigo-50' : ''
                            }`}
                            onClick={() => {
                              setCandidateSelectedIds(prev =>
                                prev.includes(c.id)
                                  ? prev.filter(id => id !== c.id)
                                  : [...prev, c.id]
                              );
                            }}
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={candidateSelectedIds.includes(c.id)}
                                readOnly
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCandidateSelectedIds(prev =>
                                    prev.includes(c.id)
                                      ? prev.filter(id => id !== c.id)
                                      : [...prev, c.id]
                                  );
                                }}
                                className="rounded border-gray-300"
                              />
                            </td>
                            <td className="px-3 py-2 text-gray-900">{customerName(c)}</td>
                            <td className="px-3 py-2 text-gray-500 text-xs">{getPhone(c) || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Right Column - Selected */}
              <div className="flex-1 flex flex-col">
                <div className="mb-2">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Selected ({selected.length})
                  </h3>
                </div>
                <input
                  type="text"
                  placeholder="Search selected..."
                  value={selectedSearch}
                  onChange={(e) => setSelectedSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={selectedRemovedIds.length === filteredSelected.length && filteredSelected.length > 0}
                      onClick={handleSelectAllSelected}
                      className="rounded border-gray-300"
                    />
                    Select All
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={handleRemoveSelected}
                      disabled={selectedRemovedIds.length === 0}
                      className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {'Remove Selected <'}
                    </button>
                    <button
                      onClick={handleRemoveAll}
                      disabled={selected.length === 0}
                      className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Remove All
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg">
                  {filteredSelected.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-400">
                      {selected.length === 0 ? 'No recipients selected yet' : 'No matches found'}
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <tbody>
                        {filteredSelected.map(c => (
                          <tr
                            key={c.id}
                            className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                              selectedRemovedIds.includes(c.id) ? 'bg-red-50' : ''
                            }`}
                            onClick={() => {
                              setSelectedRemovedIds(prev =>
                                prev.includes(c.id)
                                  ? prev.filter(id => id !== c.id)
                                  : [...prev, c.id]
                              );
                            }}
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={selectedRemovedIds.includes(c.id)}
                                readOnly
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedRemovedIds(prev =>
                                    prev.includes(c.id)
                                      ? prev.filter(id => id !== c.id)
                                      : [...prev, c.id]
                                  );
                                }}
                                className="rounded border-gray-300"
                              />
                            </td>
                            <td className="px-3 py-2 text-gray-900">{customerName(c)}</td>
                            <td className="px-3 py-2 text-gray-500 text-xs">{getPhone(c) || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving || selected.length === 0}
              className="px-6 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {saving ? 'Saving...' : `Save Selected (${selected.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
